<template>
  <div class="coloring-page">
    <div class="container">      
      <div class="canvas-container">
        <canvas 
          ref="canvasRef"
          width="1024" 
          height="740"
          @mousedown="startDrawing"
          @mousemove="draw"
          @mouseup="stopDrawing"
          @mouseleave="stopDrawing"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleToucnEnd"
          @touchcancel="handleTouchEnd"
        />
      </div>
      <div v-if="successMessage" class="success-message">
          {{ successMessage }}
      </div>

      <div class="controls">
        <div class="color-palette">
          <div 
            v-for="color in colors" 
            :key="color"
            :style="{ backgroundColor: color }"
            :class="['color-btn', { active: selectedColor === color }]"
            @click="selectColor(color)"
          />
      </div>        
      <div class="actions">
        <button @click="undo" class="btn btn-undo">Undo</button>
        <button @click="clearCanvas" class="btn btn-clear">
          Clear
        </button>
        <button @click="saveImage" class="btn btn-save">
          Save
        </button>
        <button @click="goToHomepage" class="btn btn-home">Home</button>
        
      </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDrawingsStore } from '@/stores/drawings'
import Prompt from '@/types/Prompt'


const canvasRef = ref(null)
const selectedColor = ref('#77BEF0')
const brushSize = 8
const isDrawing = ref(false)
const lastX = ref(0)
const lastY = ref(0)
const router = useRouter()
const route = useRoute()
const history = ref([])
const authStore = useAuthStore()
const imageUrl = decodeURIComponent(route.query.image || '')
const drawingStore = useDrawingsStore()
const successMessage = ref('')

let canvas = null
let ctx = null

const colors = [
  '#EA5B6F', '#F29F58', '#FEC5F6', '#FFF287',
  '#93DA97', '#77BEF0', '#3B6790', '#8F87F1',
  '#997C70', '#C7C8CC', '#000000',
]


onMounted(() => {
  initCanvas()
})


let backgroundImage = null

const initCanvas = () => {
  canvas = canvasRef.value
  ctx = canvas.getContext('2d')

  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.src = imageUrl

  img.onload = () => {
    backgroundImage = img
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
    
    history.value.push(canvas.toDataURL())
  }
  
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
}


const getMousePos = (e) => {
  const rect = canvas.getBoundingClientRect()
  return {
    x: e.clientX - rect.left,
    y: e.clientY - rect.top
  }
}

const startDrawing = (e) => {

  history.value.push(canvas.toDataURL())

  isDrawing.value = true
  const pos = getMousePos(e)
  lastX.value = pos.x
  lastY.value = pos.y
}

const draw = (e) => {
  if (!isDrawing.value) return
  
  const pos = getMousePos(e)
  
  ctx.beginPath()
  ctx.moveTo(lastX.value, lastY.value)
  ctx.lineTo(pos.x, pos.y)
  ctx.strokeStyle = selectedColor.value
  ctx.lineWidth = brushSize
  ctx.lineCap = 'round'
  ctx.stroke()
  
  lastX.value = pos.x
  lastY.value = pos.y
}

const stopDrawing = () => {
  isDrawing.value = false
  ctx.beginPath()
}

const handleTouchStart = (e) => {
  e.preventDefault()
  const touch = e.touches[0]
  const mouseEvent = new MouseEvent('mousedown', {
    clientX: touch.clientX,
    clientY: touch.clientY
  })
  startDrawing(mouseEvent)
}

const handleTouchMove = (e) => {
  e.preventDefault()
  const touch = e.touches[0]
  const mouseEvent = new MouseEvent('mousemove', {
    clientX: touch.clientX,
    clientY: touch.clientY
  })
  draw(mouseEvent)
}

const handleTouchEnd = (e) => {
  e.preventDefault()
  stopDrawing()
}

const selectColor = (color) => {
  selectedColor.value = color
}

const clearCanvas = () => {
  if (backgroundImage) {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height)
  }
}

const undo = () => {
  if (history.value.length === 0) return
  history.value.pop()


  const previous = history.value[history.value.length - 1]
  if (!previous) {
    clearCanvas()
    return
  }

  const img = new Image()
  img.onload = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  }
  img.src = previous
}

const saveImage = async (accessToken) => {
  if (!canvas) {
    console.error('Canvas not found');
    return;
  }

console.log("trying to save image!!")

const imageData = canvas.toDataURL('image/jpeg');

const payload = {
  image: imageData
}

await drawingStore.saveDrawing(authStore.session?.access_token ?? '', payload)
successMessage.value='Image saved successfully!'
setTimeout(() => successMessage.value = '', 3000)

}

const goToHomepage = () => {
  router.push("/home")
}

</script>

<style scoped>

.brush-size input {
  width: 200px;
}

.coloring-page {
  min-height: 100vh;
  padding: 20px;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

h1 {
  text-align: center;
  color: #333;
  margin-bottom: 30px;
}

.canvas-container {
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
}

canvas {
  border: 2px solid #333;
  border-radius: 8px;
  cursor: crosshair;
}

.controls {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
}

.color-palette {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.color-btn {
  width: 40px;
  height: 40px;
  border: 3px solid #ddd;
  border-radius: 15%;
  cursor: pointer;
  transition: all 0.2s;
}

.color-btn:hover {
  transform: scale(1.1);
}

.color-btn.active {
  border-color: #333;
  transform: scale(1.2);
}

.brush-size {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.brush-size input {
  width: 200px;
}

.actions {
  display: flex;
  gap: 15px;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.btn-clear {
  background-color: #42a8b4;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-save {
  background-color: #6dd496;
  color: white;
}

.btn-undo {
  background-color: #FADA7A;
  color: white;
}

.btn-home{
  background-color: #ff6b6b;
  color: white;
}

.btn-save:hover {
  background-color: #5bc482;
}

.btn-undo:hover {
  background-color: #5bc482;
}

.btn-home:hover {
  background-color: #5bc482;
}

/* Responsive design */
@media (max-width: 768px) {
  canvas {
    max-width: 100%;
    height: auto;
  }
  
  .controls {
    gap: 15px;
  }
  
  .color-palette {
    max-width: 300px;
  }
}

.success-message {
  display: block;
  width: fit-content;
  margin: 0 auto 20px auto;
  background-color: #6dd496;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 500;
  text-align: center;
}

.canvas-container canvas {
  touch-action: none; /* Prevents browser touch gestures */
  -webkit-touch-callout: none; /* Prevents callout on iOS */
  -webkit-user-select: none; /* Prevents text selection */
  user-select: none;
}

/* This is where media queries start */

/* 
@media (min-width: 768px) and (max-width: 1024px) {
  .coloring-page {
    padding: 15px;
  }

  .container {
    max-width: 95%;
    padding: 20px;
  }

  canvas {
    max-width: 90%;
    height: auto;
    
  }

  .controls {
    gap: 18px;
  }

  .color-palette {
    max-width: 400px;
    gap: 12px;
  }

  .color-btn {
    width: 45px;
    height: 45px;
    
  }

  .actions {
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
  }

  .btn {
    padding: 12px 24px;
    font-size: 16px;
    min-width: 80px;
  }
}


@media (min-width: 480px) and (max-width: 767px) {
  .coloring-page {
    padding: 10px;
  }

  .container {
    padding: 15px;
    border-radius: 5px;
  }

  canvas {
    max-width: 95%;
    height: auto;
    border-width: 1px;
  }

  .controls {
    gap: 15px;
  }

  .color-palette {
    max-width: 320px;
    gap: 8px;
  }

  .color-btn {
    width: 35px;
    height: 35px;
  }

  .actions {
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
  }

  .btn {
    padding: 10px 18px;
    font-size: 14px;
    min-width: 70px;
  }
}


@media (max-width: 479px) {
  .coloring-page {
    padding: 5px;
    min-height: 100vh;
  }

  .container {
    padding: 10px;
    border-radius: 5px;
    margin: 0;
    max-width: 100%;
  }

  .canvas-container {
    margin-bottom: 20px;
  }

  canvas {
    max-width: 100%;
    width: 100%;
    height: auto;
    border-width: 1px;
  }

  .controls {
    gap: 12px;
  }

  .color-palette {
    max-width: 280px;
    gap: 6px;
  }

  .color-btn {
    width: 30px;
    height: 30px;
    border-width: 2px;
  }

  .actions {
    flex-wrap: wrap;
    justify-content: center;
    gap: 8px;
    max-width: 100%;
  }

  .btn {
    padding: 8px 15px;
    font-size: 13px;
    min-width: 60px;
    flex: 1;
    max-width: 120px;
  }

  .success-message {
    padding: 10px 15px;
    font-size: 14px;
    margin-bottom: 15px;
  }
}


@media (max-width: 320px) {
  .container {
    padding: 8px;
  }

  .color-palette {
    max-width: 250px;
    gap: 4px;
  }

  .color-btn {
    width: 28px;
    height: 28px;
  }

  .btn {
    padding: 6px 12px;
    font-size: 12px;
    min-width: 55px;
  }

  .actions {
    gap: 6px;
  }
}


@media (max-height: 500px) and (orientation: landscape) {
  .coloring-page {
    padding: 5px;
  }

  .container {
    padding: 10px;
  }

  .canvas-container {
    margin-bottom: 15px;
  }

  canvas {
    max-height: 60vh;
    width: auto;
    max-width: 70%;
  }

  .controls {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  .color-palette {
    max-width: 200px;
  }

  .actions {
    gap: 8px;
  }

  .btn {
    padding: 6px 12px;
    font-size: 12px;
  }
}


@media (min-width: 768px) and (max-width: 1024px) and (orientation: portrait) {
  canvas {
    max-width: 85%;
    max-height: 60vh;
  }
}

@media (min-width: 768px) and (max-width: 1024px) and (orientation: landscape) {
  canvas {
    max-width: 70%;
    max-height: 70vh;
  }

  .controls {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .color-palette {
    max-width: 300px;
  }
}


@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  canvas {
    
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }
} */


</style>